if (!customElements.get('cushion-personalization')) {
  customElements.define(
    'cushion-personalization',
    class CushionPersonalization extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.toggle = this.querySelector('[data-personalization-toggle]');
        this.panel = this.querySelector('[data-personalization-panel]');
        this.radios = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.personalizationRadios = this.radios.filter((radio) => radio.value !== 'none');
        this.noneRadio = this.querySelector('[data-none-option]');
        this.choiceError = this.querySelector('[data-choice-error]');
        this.textWrap = this.querySelector('[data-text-wrap]');
        this.textInput = this.querySelector('[data-text-input]');
        this.textLabel = this.querySelector('[data-text-label]');
        this.textHelp = this.querySelector('[data-text-help]');
        this.typeProperty = this.querySelector('[data-type-property]');
        this.summary = this.querySelector('[data-summary]');
        this.total = this.querySelector('[data-total]');
        this.variantInput = this.form?.querySelector('[name="id"]');
        this.variantPrices = this.readVariantPrices();
        this.basePrice = this.variantPrices[this.variantInput?.value] || 0;

        this.currentChoice = null;
        this.toggle?.addEventListener('change', () => this.handleToggle());
        this.personalizationRadios.forEach((radio) =>
          radio.addEventListener('change', () => this.handleChoiceChange(radio))
        );
        this.textInput?.addEventListener('input', () => this.normalizeText());
        this.variantInput?.addEventListener('change', () => {
          this.basePrice = this.variantPrices[this.variantInput.value] || this.basePrice;
          this.updateTotal();
        });

        this.handleToggle();
      }

      readVariantPrices() {
        try {
          return JSON.parse(this.querySelector('[data-variant-prices]')?.textContent || '{}');
        } catch (error) {
          console.warn('No se pudieron leer los precios de las variantes.', error);
          return {};
        }
      }

      get selectedRadio() {
        return this.radios.find((radio) => radio.checked);
      }

      handleToggle() {
        const expanded = Boolean(this.toggle?.checked);
        this.panel.hidden = !expanded;
        this.toggle?.setAttribute('aria-expanded', String(expanded));

        if (expanded) {
          if (this.noneRadio?.checked) this.noneRadio.checked = false;
        } else {
          if (this.noneRadio) this.noneRadio.checked = true;
          this.currentChoice = null;
          this.textInput.value = '';
          this.choiceError.hidden = true;
        }

        this.update();
      }

      handleChoiceChange(radio) {
        if (this.currentChoice && this.currentChoice !== radio.value) this.textInput.value = '';
        this.currentChoice = radio.value;
        this.choiceError.hidden = true;
        this.update();
      }

      update() {
        const selection = this.selectedRadio;
        const personalized = Boolean(this.toggle?.checked && selection && selection.value !== 'none');
        const isInitial = selection?.value === 'applique';
        const maxLength = isInitial ? 1 : Number(this.dataset.nameMaxLength || 12);

        this.textWrap.hidden = !personalized;
        this.textInput.disabled = !personalized;
        this.textInput.required = personalized;
        this.textInput.maxLength = maxLength;
        this.typeProperty.disabled = !personalized;
        this.typeProperty.value = personalized ? selection.dataset.label : '';
        this.summary.hidden = !personalized;

        if (personalized) {
          this.textLabel.textContent = isInitial ? 'Escribe la inicial' : 'Escribe el nombre';
          this.textHelp.textContent = isInitial ? 'Máximo 1 letra.' : `Máximo ${maxLength} caracteres.`;
          this.textInput.placeholder = isInitial ? 'Ej: M' : 'Ej: Eloísa';
          this.normalizeText();
        } else {
          this.textInput.value = '';
          this.textInput.setCustomValidity('');
        }

        this.updateTotal();
      }

      normalizeText() {
        if (!this.textInput || this.textInput.disabled) return;
        if (this.selectedRadio?.value === 'applique') {
          this.textInput.value = Array.from(this.textInput.value.trim().toLocaleUpperCase('es-CL')).slice(0, 1).join('');
        }
        this.textInput.setCustomValidity('');
      }

      updateTotal() {
        const surcharge = Number(this.selectedRadio?.dataset.price || 0);
        this.total.textContent = this.formatMoney(this.basePrice + surcharge);
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: this.dataset.currency || 'CLP',
            maximumFractionDigits: 0,
          }).format(cents / 100);
        } catch (error) {
          return `$${Math.round(cents / 100).toLocaleString('es-CL')}`;
        }
      }

      validate() {
        const selection = this.selectedRadio;
        if (!this.toggle?.checked) return true;
        if (!selection || selection.value === 'none') {
          this.choiceError.hidden = false;
          this.personalizationRadios[0]?.focus();
          return false;
        }

        const text = this.textInput.value.trim();
        const isInitial = selection.value === 'applique';
        let message = '';

        if (!text) {
          message = isInitial ? 'Escribe la inicial que quieres aplicar.' : 'Escribe el nombre que quieres bordar.';
        } else if (isInitial && Array.from(text).length !== 1) {
          message = 'La opción de appliqué admite una sola inicial.';
        }

        this.textInput.setCustomValidity(message);
        if (message) {
          this.textInput.reportValidity();
          this.textInput.focus();
          return false;
        }
        return true;
      }

      getSelection() {
        const selection = this.selectedRadio;
        if (!this.toggle?.checked || !selection || selection.value === 'none') return null;

        return {
          variantId: Number(selection.dataset.variantId),
          type: selection.dataset.label,
          text: this.textInput.value.trim(),
        };
      }
    }
  );
}
